import {
  verifyAddress,
  type VerifyAddressRequest,
  type VerifyAddressResponse,
} from "src/billinglatest/clients/service.client";
import type { PixiAddress } from "../../../hooks/usePixiWizardState";
import {
  countryHasState,
  type AddressErrorReason,
  type AddressField,
  type AddressVerifyIssue,
} from "./validation";

/**
 * Map the wizard's `PixiAddress` onto the billing-client address-verify request
 * (`POST /addresses/verify`). US/CA send `stateCode` (their state field holds a
 * code); every other country sends free-text `state`.
 *
 * `includeStreet` controls whether the real street is sent — true only for the
 * address we physically ship to. For an invoicing-only billing address we send
 * an EMPTY `line1` (`""`, never `undefined` — the endpoint requires the key):
 * passing a specific street makes the provider geocode to it and return "valid"
 * for many non-US addresses (e.g. TH), so a wrong postal slips through, whereas
 * an empty street makes it check the postal/city/state against the country and
 * catch a postal that doesn't match the locality — the check we actually want.
 * Blanking the street also drops the high-false-positive `not_found`
 * (GRWBKK-1728).
 */
export const pixiAddressToVerifyRequest = (
  address: PixiAddress,
  includeStreet: boolean,
): VerifyAddressRequest => {
  const usesStateCode = countryHasState(address.country);
  return {
    line1: includeStreet ? address.street : "",
    line2: includeStreet ? address.building || undefined : undefined,
    city: address.city,
    stateCode: usesStateCode ? address.state || undefined : undefined,
    state: usesStateCode ? undefined : address.state || undefined,
    country: address.country,
    zip: address.postalCode,
  };
};

/** The verifier's reason codes (`postal_mismatch` | `state_mismatch` | `not_found`). */
type VerifyIssueReason = VerifyAddressResponse["issues"][number]["reason"];

/**
 * Map each backend reason onto the wizard field it should surface on. `not_found`
 * reports `field: "address"` (the whole address), which we anchor on the street
 * line — the primary address input.
 */
const REASON_MAP: Record<
  VerifyIssueReason,
  { field: AddressField; reason: AddressErrorReason }
> = {
  postal_mismatch: { field: "postalCode", reason: "postal_mismatch" },
  state_mismatch: { field: "state", reason: "state_mismatch" },
  not_found: { field: "street", reason: "address_not_found" },
};

export interface AddressToVerify {
  form: "billing" | "shipping";
  address: PixiAddress;
  /**
   * Whether to send the real street (`line1`). True only for the address we
   * physically ship to; an invoicing-only billing address sends an empty street
   * so the provider verifies the postal against the locality instead.
   */
  includeStreet: boolean;
}

/**
 * Verify the given addresses against the backend and return a flat list of
 * per-field issues (empty when everything is valid). Each address is mapped to
 * the verify request, sent in parallel, and every returned issue is tagged with
 * its form + mapped onto a wizard field. The underlying `verifyAddress` soft-
 * fails on error, so a verifier outage yields no issues (allow-through).
 */
export const verifyAddresses = async (
  inputs: AddressToVerify[],
  spaceId?: string,
): Promise<AddressVerifyIssue[]> => {
  const responses = await Promise.all(
    inputs.map((input) =>
      verifyAddress(
        pixiAddressToVerifyRequest(input.address, input.includeStreet),
        spaceId,
      ),
    ),
  );
  return responses.flatMap((response, index) =>
    (response.issues ?? []).map((issue) => {
      const mapped = REASON_MAP[issue.reason] ?? REASON_MAP.not_found;
      return {
        form: inputs[index].form,
        field: mapped.field,
        reason: mapped.reason,
      };
    }),
  );
};
